/**
 * Split an assistant reply into text runs and data-block markers.
 *
 * Typed data blocks stay in the message text as `[data:t1]` markers (see
 * `spliceDataBlocks` in `useAgentChat.ts`); this is where they become
 * render positions. Text runs go to react-markdown, block runs to the
 * kind registry.
 */

/** A complete marker, capturing its block key. Swallows one trailing
 * newline so the marker's own line doesn't leave a blank paragraph. */
const MARKER_RE = /\[data:(t\d+)\]\n?/g;

/**
 * A *proper prefix* of a marker sitting at the very end of the text —
 * `[`, `[da`, `[data:t1`… A marker routinely arrives split across SSE
 * chunks, and rendering the first half would flash raw `[data:` at the
 * user for a frame.
 */
const PARTIAL_MARKER_RE = /\[(?:d(?:a(?:t(?:a(?::(?:t\d*)?)?)?)?)?)?$/;

export type MessageSegment =
  | { type: "text"; text: string }
  | { type: "block"; key: string };

export interface SplitOptions {
  /**
   * While the reply is still streaming, hold back a trailing partial
   * marker instead of rendering it. Off for a finalized message, where a
   * lone `[` is just text the model wrote.
   */
  streaming?: boolean;
}

/**
 * Is the marker at `index` on a line of its own?
 *
 * Only a block-position marker becomes a component: one sitting inside a
 * list item, a table cell, or mid-sentence would be lifted out of its
 * surrounding markdown and break the structure around it (a numbered list
 * would restart, a quote would end early). The render tool instructs the
 * model to place markers on their own line, but nothing enforces it, so the
 * splitter checks rather than assumes.
 */
export function isBlockPosition(
  text: string,
  index: number,
  length: number,
): boolean {
  const before = text.slice(0, index);
  const after = text.slice(index + length);
  const startsLine = before === "" || before.endsWith("\n");
  const endsLine = after === "" || after.startsWith("\n");
  return startsLine && endsLine;
}

/**
 * Marker keys in *content* that are NOT at block position, and so must be
 * spliced as markdown inside their surrounding text rather than rendered as
 * a component.
 */
export function inlineMarkerKeys(content: string): Set<string> {
  const keys = new Set<string>();
  MARKER_RE.lastIndex = 0;
  let match = MARKER_RE.exec(content);
  while (match !== null) {
    // A marker whose match swallowed a trailing newline still ends its line.
    const consumed = match[0];
    const withoutNewline = consumed.replace(/\n$/, "");
    if (!isBlockPosition(content, match.index, withoutNewline.length)) {
      keys.add(match[1]);
    }
    match = MARKER_RE.exec(content);
  }
  return keys;
}

export function splitMessageSegments(
  content: string,
  { streaming = false }: SplitOptions = {},
): MessageSegment[] {
  if (!content) return [];

  const text = streaming ? content.replace(PARTIAL_MARKER_RE, "") : content;
  const segments: MessageSegment[] = [];
  let cursor = 0;

  MARKER_RE.lastIndex = 0;
  let match = MARKER_RE.exec(text);
  while (match !== null) {
    if (match.index > cursor) {
      segments.push({ type: "text", text: text.slice(cursor, match.index) });
    }
    segments.push({ type: "block", key: match[1] });
    cursor = match.index + match[0].length;
    match = MARKER_RE.exec(text);
  }

  if (cursor < text.length) {
    segments.push({ type: "text", text: text.slice(cursor) });
  }
  return segments;
}
